import { makeAPIRequest } from './studentRequests'
import { pullUserFromServer } from '../logic/pullUserFromServer'
import moment from 'moment/moment'

export const getAllClassrooms = async (teacherId: number): Promise<any> => {
  let response: Response
  try {
    response = await makeAPIRequest(`/classrooms?teacherUserId=${teacherId}`, 'GET')
    if (response.status !== 200) {
      throw new Error(response.statusText)
    }
    return response.json()
  } catch (e) {
    throw new Error(e as string)
  }
}

export const getContentfulCourseId = async (): Promise<string> => {
  let response: Response
  try {
    response = await makeAPIRequest(`/courses/contentfulId`, 'GET')
    if (response.status !== 200) {
      throw new Error(response.statusText)
    }
    return (await response.json()).contentfulId
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postNewClassroom = async ({
  className,
  subject,
  emoji,
  schoolId,
  startDate,
  teacherUserId,
  blackoutDays = [],
  defaultSchedule = true,
}: {
  className: string
  subject: string
  emoji: string
  schoolId: number
  startDate: string
  teacherUserId: number
  blackoutDays: string[]
  defaultSchedule?: boolean
}): Promise<any> => {
  const data = {
    name: className,
    subject,
    emoji,
    schoolId: +schoolId,
    startDate,
    teacherUserId: +teacherUserId,
    term: '',
    blackoutDays,
    defaultSchedule,
  }
  try {
    const response = await makeAPIRequest('/classrooms', 'POST', data)
    return (await response.json())
  } catch (e) {
    throw new Error(e as string)
  }
}

export const updateClassroom = async ({
  classroomId,
  className,
  subject,
  emoji,
  startDate,
  blackoutDays = [],
  defaultSchedule = true,
  lastInteractedAt = null,
}: {
  classroomId: number
  className: string
  subject: string
  emoji: string
  startDate: string
  blackoutDays: string[]
  defaultSchedule: boolean
  lastInteractedAt?: string | null
}): Promise<any> => {
  const data = {
    name: className,
    subject,
    emoji,
    startDate,
    blackoutDays,
    defaultSchedule,
    lastInteractedAt,
  }
  let response: Response
  try {
    response = await makeAPIRequest(`/classrooms/${classroomId}`, 'PUT', data)
    return response.json()
  } catch (e) {
    throw new Error(e as string)
  }
}

export const validateStudent = async (validationToken: string): Promise<[boolean, boolean | string]> => {
  const data = {
    token: validationToken,
  }

  try {
    const response = await makeAPIRequest('/users/validate', 'POST', data)
    if (response.status === 200) {
      return [true, true]
    } else {
      return [false, (await response.json()).message]
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const getStudentsForSchool = async (schoolId: number, classroomId?: number): Promise<any> => {
  try {
    const response = await makeAPIRequest(`/users?schoolId=${schoolId}${classroomId ? `&classroomId=${classroomId}` : ''}`, 'GET')
    return response.json()
  } catch (e) {
    throw new Error(e as string)
  }
}

export const generatePasswordResetLink = async (classroomId: number, studentId: number): Promise<string> => {
  try {
    const response = await makeAPIRequest(`/classrooms/${classroomId}/user/${studentId}/passwordReset`, 'PUT')
    return (await response.json()).url
  } catch (e) {
    throw new Error(e as string)
  }
}

export const deleteStudent = async (studentId: number): Promise<[boolean, boolean | string]> => {
  try {
    const response = await makeAPIRequest(`/users/${studentId}`, 'DELETE')
    if (response.status === 200) {
      return [true, true]
    } else {
      return [false, (await response.json()).message]
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const removeStudent = async (classroomId: number, studentId: number): Promise<[boolean, boolean | string]> => {
  try {
    const response = await makeAPIRequest(`/classrooms/${classroomId}/user/${studentId}/removeStudent`, 'PUT')
    if (response.status === 200) {
      return [true, true]
    } else {
      return [false, (await response.json()).message]
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const resetStudentMode = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/teacher/resetPreview', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error resetting your course. Please try again.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postCompleteTeacherOrientation = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/completeTeacherOrientation', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const dismissStudentViewCallout = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/dismissStudentViewCallout', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const setSeenWelcomeToStudentViewToTrue = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/setSeenWelcomeToStudentViewToTrue', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const setSeenAgeAndConsentDisclaimerStatusToHasSeen = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/setSeenAgeAndConsentDisclaimerStatusToHasSeen', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const completeContentForStudent = async ({
  studentId,
  classroomId,
  contentId,
  contentType
}: {
  studentId: number
  classroomId: number
  contentId: string
  contentType: string
}): Promise<void> => {
  try {
    const body = {
      contentId,
      contentType
    }
    const response = await makeAPIRequest(`/classrooms/${classroomId}/students/${studentId}/completeContent`, 'POST', body)
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }

}

export const moveStudent = async ({ studentId, newClassroomId, currentClassroomId }: { studentId: number, newClassroomId: number, currentClassroomId: number }): Promise<void> => {
  const data = {
    currentClassroomId,
    newClassroomId,
    studentId,
  }
  try {
    const response = await makeAPIRequest('/classrooms/moveStudent', 'PUT', data)
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const getMostRecentFocusSeshDataForEachStudentInClassroom = async (classroomId: number): Promise<any> => {
  let response: Response
  try {
    response = await makeAPIRequest(`/classrooms/${classroomId}/mostRecentFocusSeshDataForEachStudent`, 'GET')
    if (response.status !== 200) {
      throw new Error(response.statusText)
    }
    return response.json()
  } catch (e) {
    throw new Error(e as string)
  }
}

export const dismissArchivingClassesCallout = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/dismissArchivingClassesCallout', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const setSeenTenDayCourseTabInfoToTrue = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/setSeenTenDayCourseTabInfoToTrue', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const setSeenImportantInfoAboutNewClassToTrue = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/setSeenImportantInfoAboutNewClassToTrue', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const archiveClassroom = async (classroomId: number): Promise<void> => {
  try {
    const response = await makeAPIRequest(`/classrooms/${classroomId}/archive`, 'PUT')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const restoreClassroom = async (classroomId: number): Promise<void> => {
    try {
        const response = await makeAPIRequest(`/classrooms/${classroomId}/restore`, 'PUT')
      if (response.status !== 200) {
        throw new Error('There was an error.')
      }
    } catch (e) {
        throw new Error(e as string)
    }
}

export const getInitialLessonsForClassroom = async (classroomId: number): Promise<any> => {
  try {
    const response = await makeAPIRequest(`/userLessons/${classroomId}/lessons`, 'GET')
    if (response.status !== 200) {
      throw new Error()
    }
    return await response.json()
  } catch (e) {
    throw new Error()
  }
}

export const getInitialDailyBeatsForClassroom = async (classroomId: number): Promise<any> => {
  try {
    const response = await makeAPIRequest(`/classrooms/${classroomId}/dailyVideosV2`, 'GET')
    if (response.status !== 200) {
      throw new Error()
    }
    return await response.json()
  } catch (e) {
    throw new Error()
  }
}

export const initializeAIThread = async (data: Record<string, any>): Promise<any> => {
  try {
    const response = await makeAPIRequest('/openAI/initThread', 'POST', data)
    if (response.status !== 200) {
      throw new Error()
    }
    return await response.json()
  } catch (e) {
      throw new Error(e as string)
  }
}

export const streamAIResponse = (threadId: string): EventSource => {
  const DOMAIN = process.env.NEXT_PUBLIC_BACKEND_DOMAIN
  try {
    return new EventSource(`${DOMAIN}/openAI/streamAssistantResponse?threadId=${threadId}`)
  } catch (e) {
    throw new Error(e as string)
  }
}

export const createAISupportQueryResponseLog = async (data: Record<string, any>): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/createAISupportQueryResponseLog', 'POST', data)
    if (response.status !== 200) {
      throw new Error()
    }
  } catch (e) {
      throw new Error()
  }
}

export const postDismissAISupportFeatureTag = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/dismissAISupportFeatureTag', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
    await pullUserFromServer()
  } catch (e) {
    throw new Error(e as string)
  }
}

export const setSeenPreviousCourseProgressWarning = async (classroomId: number): Promise<void> => {
  try {
    const response = await makeAPIRequest(`/classrooms/setSeenPreviousCourseProgressWarning/${classroomId}`, 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const getStudentsAndClassroomDataFromClassroomId = async (classroomId: number): Promise<any> => {
    try {
      const response = await makeAPIRequest(`/classrooms/${classroomId}/getStudentsAndClassroomFromClassroomId`, 'GET')
      if (response.status !== 200) {
        throw new Error()
      }
      return await response.json()
    } catch (e) {
        throw new Error()
    }
}

export const postDismissAutoArchivedClassesNotice = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/dismissAutoArchivedClassesNotice', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const setClassroomLastInteractedAt = async (classroomId: number): Promise<void> => {
  try {
    const lastInteractedAt = moment().utc().toISOString()
    const data = {
      classroomId,
      lastInteractedAt,
    }
    const response = await makeAPIRequest(`/classrooms/setLastInteractedAt`, 'POST', data)
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
      throw new Error(e as string)
  }
}

export const resetCourseProgressForStudents = async (classroomId: number, studentIds: number[], courseId: string): Promise<void> => {
  try {
    const data = {
      classroomId,
      studentIds,
      courseId, // provide for targeting one course. omit for deleting all course progress
    }
    const response = await makeAPIRequest(`/classrooms/resetCourseProgressForStudentsInClassroom`, 'POST', data)
    if (response.status !== 200) {
      throw new Error('There was an error resetting course progress.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postSeenClassLinkingTeacherInfo = async (): Promise<void> => {
  try {
    const response = await makeAPIRequest('/users/setSeenClassLinkingTeacherInfo', 'POST')
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
    await pullUserFromServer()
  } catch (e) {
    throw new Error(e as string)
  }
}

export const postLastVisitedTabIndex = async (classroomId: number, lastVisitedTabIndex: number): Promise<void> => {
    try {
        const data = {
          classroomId,
          lastVisitedTabIndex,
        }
        const response = await makeAPIRequest(`/classrooms/setLastVisitedTabIndex`, 'POST', data)
        if (response.status !== 200) {
          throw new Error('There was an error.')
        }
    } catch (e) {
        throw new Error(e as string)
    }
}

export const saveLastCustomFocusSeshSettingsForClassroom = async (classroomId: number, seshSettings: Record<string, any>): Promise<void> => {
  try {
    const data = {
      classroomId,
      seshSettings,
    }
    const response = await makeAPIRequest(`/classrooms/saveLastCustomFocusSeshSettings`, 'POST', data)
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

export const createProjectedSessionFocusSeshLogForStudentsInClassroom = async ({
  classroomId,
  excludedStudentIds,
  sessionData,
}:{
  classroomId: number,
  excludedStudentIds: number[],
  sessionData: any
}) => {
  try {
    const data = {
      classroomId,
      excludedStudentIds,
      sessionData,
    }
    const response = await makeAPIRequest(`/classrooms/createProjectedSessionFocusSeshLogForStudentsInClassroom`, 'POST', data)
    if (response.status !== 200) {
      throw new Error('There was an error.')
    }
  } catch (e) {
    throw new Error(e as string)
  }
}

